import {boundMethod} from "autobind-decorator";
import React from "react";
import {DragDropContext, DropResult} from "react-beautiful-dnd";

import http from "@/services/http";
import {ERoles} from "@/services/models";
import session from "@/services/session";

import {IsDraggingContext} from "@shared/data-ranged-analyzed/models";
import DeleteButton from "@toolbox/button-like/DeleteButton";
import DragDropRow from "@toolbox/display-blocks/DragDropRow";
import {CancelButton} from "@toolbox/modals/Modal";
import T from "@translate/T";
import {IQueuedSopModel, IQueueSopSortOrderRequest} from "./models";
import SopInfo from "./SopInfo";

interface IManageSopQueueProps {
    items: IQueuedSopModel[];
}

interface IManageSopQueueState {
    isDragging: boolean;
}

class ManageSopQueue extends React.PureComponent<
    IManageSopQueueProps,
    IManageSopQueueState
> {
    public readonly state: IManageSopQueueState = {
        isDragging: false,
    };

    @boundMethod
    public onBeforeDragStart() {
        this.setState({isDragging: true});
    }

    @boundMethod
    public onDragEnd(result: DropResult) {
        this.setState({isDragging: false});

        // dropped outside the list
        if (!result.destination) {
            return;
        }

        this.moveSop(result.source.index, result.destination.index);
    }

    @boundMethod
    private moveSop(fromIndex: number, toIndex: number) {
        const {items} = this.props;

        const moved = items.splice(fromIndex, 1)[0];
        items.splice(toIndex, 0, moved);

        this.reorderQueue(items);
    }

    public render() {
        return (
            <React.Fragment>
                <table
                    id="sop-queue-table"
                    className="table table-sm table-striped table-borderless sop-queue-table"
                >
                    <thead>
                        <tr>
                            <th>
                                <T>SOP</T>
                            </th>
                            <th>
                                <T>Device</T>
                            </th>
                            <th>
                                <T>Time</T>
                            </th>
                            <th>
                                <T>User</T>
                            </th>
                            {session.hasRole(ERoles.Recorder) && (
                                <th>
                                    <T>Actions</T>
                                </th>
                            )}
                        </tr>
                    </thead>
                    {this.renderBody()}
                </table>
                {this.renderFooter()}
            </React.Fragment>
        );
    }

    private renderBody() {
        return (
            <DragDropContext
                onDragEnd={this.onDragEnd}
                onBeforeDragStart={this.onBeforeDragStart}
            >
                {this.renderQueuedItems()}
            </DragDropContext>
        );
    }

    private renderQueuedItems() {
        const {isDragging} = this.state;
        const {items} = this.props;
        return (
            <IsDraggingContext.Provider value={isDragging}>
                <DragDropRow<IQueuedSopModel>
                    values={items}
                    renderContent={this.renderQueue}
                />
            </IsDraggingContext.Provider>
        );
    }

    private renderQueue(sop: IQueuedSopModel) {
        return (
            <IsDraggingContext.Consumer>
                {(isDragging: boolean) => (
                    <SopInfo key={sop.id} sop={sop} isDragging={isDragging} />
                )}
            </IsDraggingContext.Consumer>
        );
    }

    private renderFooter() {
        if (!session.hasRole(ERoles.Recorder)) {
            return null;
        }

        return (
            <div className="modal-footer">
                <DeleteButton
                    id="remove-all"
                    onDelete={this.clear}
                    otherName={<T>Remove all</T>}
                />

                <CancelButton>
                    <T>Close</T>
                </CancelButton>
            </div>
        );
    }

    @boundMethod
    private async clear() {
        const {items} = this.props;
        let success = true;
        for (const {id} of items) {
            try {
                await http.delete(`/api/centrifuge/sop/queue/${id}`);
            } catch {
                success = false;
            }
        }

        return success;
    }

    @boundMethod
    private async reorderQueue(reorderedItems: IQueuedSopModel[]) {
        const json: IQueueSopSortOrderRequest[] = reorderedItems.map(
            (sop, i) => ({id: sop.id, manualSortOrder: i + 1}),
        );

        http.put("/api/centrifuge/sop/queue/sort-order", {json});
    }
}

export default ManageSopQueue;
